import { inject, Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { AttachmentViewModalComponent } from '../../../../_shared/components/modals/attachment-view-modal/attachment-view-modal.component';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { VehicleDocument } from '../../../asset-overview/models/vehicle-documents.model';
import { VehicleDocumentsService } from '../../../asset-overview/services/vehicle-documents.service';
import { RoutineChecklistRequirementLinkedDocument } from '../../compliance-overview/models/inspection-checklist.model';
import { buildLinkedDocumentLabel } from '../helpers/linked-document.helper';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root'
})
export class LinkedDocumentService {
  private readonly vehicleDocumentsService = inject(VehicleDocumentsService);
  private readonly errorService = inject(ErrorHandlerService);
  private readonly modalService = inject(NgbModal);
  private readonly toastr = inject(ToastrService);
  private readonly transloco = inject(TranslocoService);
  private isViewerActive = false;

  async withDocumentAsync(
    assetId: number,
    documentId: number,
    onFound: (document: VehicleDocument) => void,
    cancellation: Subject<void> = new Subject<void>()
  ): Promise<void> {
    try {
      const response = await this.vehicleDocumentsService.getDocumentsByAssetId(assetId, cancellation);
      const vehicleDocument = (response.data ?? []).find(document => document.documentId === documentId);

      if (vehicleDocument) {
        onFound(vehicleDocument);
      } else {
        this.toastr.error(this.transloco.translate('compliance.shared.toasts.linkedDocumentNotFound'));
      }
    } catch (error) {
      this.errorService.handleError(error, { showToast: true });
    }
  }

  async openAsync(
    assetId: number,
    document: RoutineChecklistRequirementLinkedDocument,
    cancellation?: Subject<void>
  ): Promise<void> {
    if (this.isViewerActive) {
      return;
    }

    if (!document.viewUrl) {
      await this.openByDocumentIdAsync(assetId, document.documentId, cancellation);
      return;
    }

    this.isViewerActive = true;
    this.openViewer(document.viewUrl, document.downloadUrl ?? null, buildLinkedDocumentLabel(document));
  }

  async openByDocumentIdAsync(assetId: number, documentId: number, cancellation?: Subject<void>): Promise<void> {
    if (this.isViewerActive) {
      return;
    }

    this.isViewerActive = true;
    let opened = false;

    try {
      await this.withDocumentAsync(assetId, documentId, document => {
        this.openViewer(document.viewUrl, document.downloadUrl, `${document.documentName}${document.extension ?? ''}`);
        opened = true;
      }, cancellation);
    } finally {
      if (!opened) {
        this.isViewerActive = false;
      }
    }
  }

  private openViewer(fileUrl: string, downloadUrl: string | null, fileName: string): void {
    const modalRef = this.modalService.open(AttachmentViewModalComponent, { centered: true, size: 'lg' });
    modalRef.componentInstance.fileUrl = fileUrl;
    modalRef.componentInstance.downloadUrl = downloadUrl;
    modalRef.componentInstance.fileName = fileName;
    modalRef.hidden.subscribe(() => {
      this.isViewerActive = false;
    });
  }
}